import { MediaFile } from '../services/mediaService';

export interface AdStudioFormData {
  // Basic form fields
  prompt: string;
  adIntent: string;
  product_features?: string;
  product_features_title?: string;
  formatComposition: string;
  styleArtDirection: string;
  platformPlacement: string;
  num_images: number;
  preserve_subject: boolean;
  
  // Text overlay fields
  enable_text_overlay: boolean;
  text_overlay_content: string;
  font_family: string;
  font_size: string;
  font_color: string;
  text_position: string;
  
  // Multi-product fields
  product_arrangement?: string;
  product_relationship?: string;
  product_priorities?: number[];
  product_labels?: string[];
  background_complexity?: string;
  color_harmony?: string;
  
  // Reference image fields
  reference_style_weight?: number;
  reference_composition_weight?: number;
  
  // Influence tuning
  influenceTuning: { [key: string]: number };
}

export interface AdStudioState {
  // Form data
  formData: AdStudioFormData;
  
  // Mode states
  multiProductMode: boolean;
  textOnlyMode: boolean;
  useBrandify: boolean;
  showAdvanced: boolean;
  showInfluenceTuning: boolean;
  
  // Selected media
  selectedImage: MediaFile[];
  selectedProducts: MediaFile[];
  selectedReferenceImages: MediaFile[];
  selectedCredibilityImages: MediaFile[];
  
  // Text prompt for text-only mode
  textPrompt: string;
  
  // Generation results
  generatedImages: string[];
  
  // Generation timing state
  generationProgress?: number;
  generationStartTime?: number | null;
  
  // AR mode data (if applicable)
  isARMode?: boolean;
  arViewerUrl?: string;
  inputImageUrl?: string;
  sourceMediaId?: string;
  
  // Session tracking
  sessionId?: string;
  
  // User identification for security
  userId?: string;
  clientId?: string;
  
  // Timestamp for cleanup
  lastUpdated: string;
}

const STORAGE_KEY_PREFIX = 'adStudio_state';
const SESSION_KEY_PREFIX = 'adStudio_sessionId';
const STORAGE_EXPIRY_HOURS = 24; // State expires after 24 hours

// Get user-specific storage keys
const getUserStorageKey = (userId?: string, clientId?: string): string => {
  if (!userId || !clientId) {
    // If no user/client info, use a temporary key that will be cleared
    return `${STORAGE_KEY_PREFIX}_temp`;
  }
  return `${STORAGE_KEY_PREFIX}_${userId}_${clientId}`;
};

const getUserSessionKey = (userId?: string, clientId?: string): string => {
  if (!userId || !clientId) {
    return `${SESSION_KEY_PREFIX}_temp`;
  }
  return `${SESSION_KEY_PREFIX}_${userId}_${clientId}`;
};

export class AdStudioPersistence {
  private static currentSessionId: string | null = null;
  private static currentUserId: string | null = null;
  private static currentClientId: string | null = null;
  
  // Set user context for all operations
  static setUserContext(userId: string, clientId: string): void {
    const previousUserId = this.currentUserId;
    const previousClientId = this.currentClientId;
    
    this.currentUserId = userId;
    this.currentClientId = clientId;
    console.log('👤 Ad Studio user context set:', { userId, clientId, previousUserId, previousClientId });
    
    // If this is a different user, clear all Ad Studio states to prevent data leakage
    if (previousUserId && previousUserId !== userId) {
      console.log('🗑️ Different user detected, clearing all Ad Studio states');
      this.clearAllAdStudioStates();
    }
    
    // Clear any temporary state when user context is set
    this.clearTemporaryState();
  }
  
  // Clear user context (on logout)
  static clearUserContext(): void {
    console.log('👤 Clearing Ad Studio user context');
    this.currentUserId = null;
    this.currentClientId = null;
    this.currentSessionId = null;
    
    // Clear any temporary state
    this.clearTemporaryState();
  }
  
  // Clear temporary state used when no user context
  private static clearTemporaryState(): void {
    try {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}_temp`);
      sessionStorage.removeItem(`${SESSION_KEY_PREFIX}_temp`);
    } catch (error) {
      console.warn('Failed to clear temporary state:', error);
    }
  }
  
  // Generate a unique session ID for this page visit
  static initializeSession(): string {
    this.currentSessionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const sessionKey = getUserSessionKey(this.currentUserId || undefined, this.currentClientId || undefined);
    sessionStorage.setItem(sessionKey, this.currentSessionId);
    console.log('🆔 New Ad Studio session initialized:', this.currentSessionId, 'for user:', this.currentUserId);
    return this.currentSessionId;
  }
  
  // Check if this is the same session (same page visit)
  static isSameSession(): boolean {
    const sessionKey = getUserSessionKey(this.currentUserId || undefined, this.currentClientId || undefined);
    const storedSessionId = sessionStorage.getItem(sessionKey);
    return storedSessionId === this.currentSessionId;
  }
  
  // Check if we should restore based on navigation (not just session)
  static shouldRestoreFromNavigation(): boolean {
    // Only check for state if we have user context
    if (!this.currentUserId || !this.currentClientId) {
      console.warn('⚠️ No user context for state restoration');
      return false;
    }
    
    const storageKey = getUserStorageKey(this.currentUserId, this.currentClientId);
    const hasStoredState = localStorage.getItem(storageKey) !== null;
    
    // Always restore if we have stored state - let the component decide if it should use it
    return hasStoredState;
  }
  
  // Only save state when user is about to leave or has left
  static saveStateOnNavigation(state: Partial<AdStudioState>): void {
    // Don't save if no user context
    if (!this.currentUserId || !this.currentClientId) {
      console.warn('⚠️ Cannot save state without user context');
      return;
    }
    
    try {
      const currentState = this.loadState();
      const updatedState: AdStudioState = {
        ...currentState,
        ...state,
        lastUpdated: new Date().toISOString(),
        sessionId: this.currentSessionId,
        userId: this.currentUserId, // Store user ID in state for validation
        clientId: this.currentClientId, // Store client ID in state for validation
      };
      
      const storageKey = getUserStorageKey(this.currentUserId, this.currentClientId);
      localStorage.setItem(storageKey, JSON.stringify(updatedState));
      console.log('💾 Ad Studio state saved for navigation:', updatedState);
    } catch (error) {
      console.error('Failed to save Ad Studio state:', error);
    }
  }
  
  // Regular save during same session (lighter, no persistence)
  static saveState(state: Partial<AdStudioState>): void {
    // Only save to localStorage if we're in a different session
    if (!this.isSameSession()) {
      this.saveStateOnNavigation(state);
    }
    // For same session, we don't need to persist to localStorage
  }
  
  static loadState(): AdStudioState {
    try {
      // Don't load if no user context
      if (!this.currentUserId || !this.currentClientId) {
        console.warn('⚠️ Cannot load state without user context');
        return this.getDefaultState();
      }
      
      // AGGRESSIVE CHECK: Clear any foreign state before loading
      this.clearForeignStates();
      
      const storageKey = getUserStorageKey(this.currentUserId, this.currentClientId);
      const saved = localStorage.getItem(storageKey);
      if (!saved) {
        console.log('📝 No saved state for current user, returning defaults');
        return this.getDefaultState();
      }
      
      const state: AdStudioState = JSON.parse(saved);
      
      // Validate that the state belongs to the current user
      if (state.userId && state.userId !== this.currentUserId) {
        console.warn('⚠️ State belongs to different user, clearing:', { stateUserId: state.userId, currentUserId: this.currentUserId });
        this.clearState();
        return this.getDefaultState();
      }
      
      if (state.clientId && state.clientId !== this.currentClientId) {
        console.warn('⚠️ State belongs to different client, clearing:', { stateClientId: state.clientId, currentClientId: this.currentClientId });
        this.clearState();
        return this.getDefaultState();
      }
      
      // Check if state has expired
      if (this.isStateExpired(state)) {
        console.log('🗑️ Ad Studio state expired, clearing...');
        this.clearState();
        return this.getDefaultState();
      }
      
      console.log('📂 Ad Studio state loaded for user:', this.currentUserId, state);
      return state;
    } catch (error) {
      console.error('Failed to load Ad Studio state:', error);
      return this.getDefaultState();
    }
  }
  
  // Clear any states that don't belong to current user
  private static clearForeignStates(): void {
    try {
      const allKeys = Object.keys(localStorage);
      const currentUserKey = getUserStorageKey(this.currentUserId!, this.currentClientId!);
      
      // Find all Ad Studio state keys that don't belong to current user
      const foreignStateKeys = allKeys.filter(key => 
        key.startsWith(STORAGE_KEY_PREFIX) && key !== currentUserKey
      );
      
      if (foreignStateKeys.length > 0) {
        console.log('🗑️ Clearing foreign states during load:', foreignStateKeys);
        foreignStateKeys.forEach(key => {
          localStorage.removeItem(key);
        });
      }
    } catch (error) {
      console.error('Failed to clear foreign states:', error);
    }
  }
  
  // Check if we should restore state (only on new session)
  static shouldRestoreState(): boolean {
    return this.shouldRestoreFromNavigation();
  }
  
  static clearState(): void {
    try {
      if (this.currentUserId && this.currentClientId) {
        const storageKey = getUserStorageKey(this.currentUserId, this.currentClientId);
        localStorage.removeItem(storageKey);
        console.log('🗑️ Ad Studio state cleared for user:', this.currentUserId);
      } else {
        // Clear temporary state if no user context
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}_temp`);
        console.log('🗑️ Ad Studio temporary state cleared');
      }
    } catch (error) {
      console.error('Failed to clear Ad Studio state:', error);
    }
  }
  
  static updateFormData(formData: Partial<AdStudioFormData>): void {
    const currentState = this.loadState();
    this.saveState({
      formData: {
        ...currentState.formData,
        ...formData,
      },
    });
  }
  
  static updateSelectedMedia(
    selectedImage?: MediaFile[],
    selectedProducts?: MediaFile[],
    selectedReferenceImages?: MediaFile[],
    selectedCredibilityImages?: MediaFile[]
  ): void {
    const updates: Partial<AdStudioState> = {};
    
    if (selectedImage !== undefined) {
      updates.selectedImage = selectedImage;
    }
    if (selectedProducts !== undefined) {
      updates.selectedProducts = selectedProducts;
    }
    if (selectedReferenceImages !== undefined) {
      updates.selectedReferenceImages = selectedReferenceImages;
    }
    if (selectedCredibilityImages !== undefined) {
      updates.selectedCredibilityImages = selectedCredibilityImages;
    }
    
    this.saveState(updates);
  }
  
  static updateGenerationResults(generatedImages: string[]): void {
    this.saveState({ generatedImages });
  }
  
  static updateModeStates(modes: {
    multiProductMode?: boolean;
    textOnlyMode?: boolean;
    useBrandify?: boolean;
    showAdvanced?: boolean;
    showInfluenceTuning?: boolean;
  }): void {
    this.saveState(modes);
  }
  
  static updateTextPrompt(textPrompt: string): void {
    this.saveState({ textPrompt });
  }
  
  static setARMode(arData: {
    isARMode: boolean;
    arViewerUrl?: string;
    inputImageUrl?: string;
    sourceMediaId?: string;
  }): void {
    this.saveState(arData);
  }
  
  private static isStateExpired(state: AdStudioState): boolean {
    if (!state.lastUpdated) return true;
    
    const lastUpdated = new Date(state.lastUpdated);
    const now = new Date();
    const hoursDiff = (now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60);
    
    return hoursDiff > STORAGE_EXPIRY_HOURS;
  }
  
  private static getDefaultState(): AdStudioState {
    return {
      formData: {
        prompt: '',
        adIntent: '',
        formatComposition: '',
        styleArtDirection: '',
        platformPlacement: '',
        num_images: 1,
        preserve_subject: true,
        product_features: '',
        product_features_title: '',
        enable_text_overlay: false,
        text_overlay_content: '',
        font_family: 'auto',
        font_size: 'auto',
        font_color: 'auto',
        text_position: 'auto',
        background_complexity: 'minimal',
        color_harmony: 'auto',
        reference_style_weight: 0.7,
        reference_composition_weight: 0.5,
        influenceTuning: {},
      },
      multiProductMode: false,
      textOnlyMode: false,
      useBrandify: false,
      showAdvanced: false,
      showInfluenceTuning: false,
      selectedImage: [],
      selectedProducts: [],
      selectedReferenceImages: [],
      selectedCredibilityImages: [],
      textPrompt: '',
      generatedImages: [],
      lastUpdated: new Date().toISOString(),
    };
  }
  
  // Utility method to check if there's a generation in progress
  static hasActiveGeneration(): boolean {
    if (!this.shouldRestoreState()) return false;
    
    const state = this.loadState();
    return state.generatedImages.length === 0 && (
      state.selectedImage.length > 0 || 
      state.selectedProducts.length > 0 || 
      state.textPrompt.length > 0
    );
  }
  
  // Check if we have persisted state that should be shown to user
  static hasPersistedStateToRestore(): boolean {
    return this.shouldRestoreState() && (
      this.hasActiveGeneration() || 
      this.loadState().generatedImages.length > 0
    );
  }
  
  // Utility method to get a summary of current state for debugging
  static getStateSummary(): string {
    const state = this.loadState();
    return `Ad Studio State: ${state.multiProductMode ? 'Multi-Product' : state.textOnlyMode ? 'Text-Only' : 'Single-Product'} mode, ${state.selectedImage.length + state.selectedProducts.length} images selected, ${state.generatedImages.length} results, Brandify: ${state.useBrandify ? 'ON' : 'OFF'}`;
  }
  
  // Setup beforeunload listener to save state when user navigates away
  static setupNavigationListener(): void {
    const handleBeforeUnload = () => {
      console.log('🚪 User navigating away, saving current state...');
      // This will be called by the component when user leaves
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }
  
  // Clear all Ad Studio states from localStorage (aggressive cleanup)
  private static clearAllAdStudioStates(): void {
    try {
      const keys = Object.keys(localStorage);
      const adStudioKeys = keys.filter(key => key.startsWith(STORAGE_KEY_PREFIX));
      
      adStudioKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Also clear completed task IDs
      const completedTaskKeys = keys.filter(key => key.includes('adStudio_completedTaskIds'));
      completedTaskKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      console.log(`🗑️ Cleared ${adStudioKeys.length + completedTaskKeys.length} Ad Studio entries for user switch`);
    } catch (error) {
      console.error('Failed to clear all Ad Studio states:', error);
    }
  }
  
  // Utility method to clear all Ad Studio states (for admin/cleanup purposes)
  static clearAllUserStates(): void {
    this.clearAllAdStudioStates();
  }
}